import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { dismissToast, useToasts, type Toast } from "@/components/shell/toast";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";

const DEFAULT_DURATION_MS = 10_000;

/**
 * Draws the toasts `showToast` raises, once, at the bottom of the shell.
 *
 * Above the sheets and dialogs (their layer is 50): a toast raised while a
 * drawer is open must not be underneath it. Centred, and at most a phone's
 * width less a gutter, so the same toast reads on 390px and on 1440px without
 * a second layout.
 *
 * The region is `aria-live`, so a screen reader hears a toast arrive without
 * focus moving to it — moving focus would pull someone out of the list they
 * were working in, which is the opposite of what a note about a row that has
 * just left it is for. Each toast stays while it is hovered or holds focus, so
 * reaching for Undo is never a race with a timer.
 *
 * While a phone's navigation sheet is open the toasts step aside, timers
 * paused, and come back when it closes (#548): the foot of the sheet holds the
 * Account button, and for the toast's whole stay a tap on Account landed on the
 * toast instead.
 */
export function Toaster() {
  const toasts = useToasts();
  const { isMobile, openMobile } = useSidebar();
  const stepAside = isMobile && openMobile;

  return (
    <div
      role="region"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] data-[hidden=true]:hidden"
      data-hidden={stepAside}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} paused={stepAside} />
      ))}
    </div>
  );
}

function ToastItem({ toast, paused }: { toast: Toast; paused: boolean }) {
  const [pointerHeld, setHeld] = useState(false);
  const held = pointerHeld || paused;
  const { action, link } = toast;
  const duration = toast.durationMs ?? DEFAULT_DURATION_MS;

  useEffect(() => {
    if (held) return;
    const timer = setTimeout(() => {
      dismissToast(toast.id);
    }, duration);
    return () => {
      clearTimeout(timer);
    };
  }, [held, duration, toast.id]);

  return (
    <div
      role={toast.tone === "error" ? "alert" : "status"}
      onPointerEnter={() => {
        setHeld(true);
      }}
      onPointerLeave={() => {
        setHeld(false);
      }}
      onFocus={() => {
        setHeld(true);
      }}
      onBlur={() => {
        setHeld(false);
      }}
      className="bg-popover text-popover-foreground pointer-events-auto flex w-full max-w-md items-start gap-2 rounded-md border p-3 shadow-lg"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug font-medium">{toast.title}</p>
        {toast.description ? (
          <p className="text-muted-foreground mt-0.5 text-xs leading-snug wrap-anywhere">
            {toast.description}
          </p>
        ) : null}
        {action || link ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            {action ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                // Finger-sized where a finger is the input, on the button and
                // not only the row it sits in.
                className="pointer-coarse:h-11 pointer-coarse:px-4"
                onClick={() => {
                  dismissToast(toast.id);
                  action.onClick();
                }}
              >
                {action.label}
              </Button>
            ) : null}
            {link ? (
              <Link
                to={link.to}
                search={link.search}
                // Stays up after following it: the link is "where it went", and
                // Undo may still be wanted once you have looked (#548).
                className="text-primary-strong inline-flex items-center text-sm underline underline-offset-2 hover:no-underline pointer-coarse:min-h-11"
              >
                {link.label}
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Dismiss"
        className="text-muted-foreground -mt-0.5 -mr-1 pointer-coarse:size-11 pointer-coarse:-mt-2.5 pointer-coarse:-mr-2.5"
        onClick={() => {
          dismissToast(toast.id);
        }}
      >
        <X aria-hidden />
      </Button>
    </div>
  );
}
